import type {
	MediaAccessDto,
	MediaAssetDto,
	MediaFolderDto,
	MediaType,
	MediaUsedFilter,
} from '@supply-mover-2-0/shared';

/**
 * PD-16339 — small, pure helpers the media library screens share. Every string
 * returned here is English and is passed through `t()` by the caller.
 */

/** `1.4 MB`, `820 KB`, or null when unknown. */
export function describeMediaBytes(
	bytes: number | null | undefined,
): string | null {
	if (bytes == null || !Number.isFinite(bytes) || bytes < 0) {
		return null;
	}
	if (bytes >= 1024 * 1024) {
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	}
	return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** `1500 × 900`, or null for a PDF or an item not processed yet. */
export function describeMediaDimensions(
	asset: Pick<MediaAssetDto, 'width' | 'height'>,
): string | null {
	return asset.width != null && asset.height != null
		? `${asset.width} × ${asset.height}`
		: null;
}

export const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
	image: 'Picture',
	svg: 'SVG logo',
	document: 'PDF',
};

/**
 * The where-used filter's choices, in the order offered. Only the kinds of use the
 * library records today are listed; the others (promotions, categories, brands,
 * storefront layouts) join the list when their screens record uses.
 */
export const MEDIA_USED_FILTER_OPTIONS: ReadonlyArray<{
	value: MediaUsedFilter;
	label: string;
}> = [
	{ value: 'unused', label: 'Not used anywhere' },
	{ value: 'website', label: 'Used on websites' },
	{ value: 'email_template', label: 'Used in email templates' },
];

/** A `YYYY-MM` month as the month filter shows it ("September 2026"); the value itself when it is not a month. */
export function describeMediaMonth(month: string): string {
	const match = /^(\d{4})-(\d{2})$/.exec(month);
	if (match == null) {
		return month;
	}
	return new Intl.DateTimeFormat('en-US', {
		month: 'long',
		year: 'numeric',
		timeZone: 'UTC',
	}).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
}

/** What the file input accepts for the given item types. */
export function acceptForMediaTypes(types: ReadonlyArray<MediaType>): string {
	const accept: Array<string> = [];
	if (types.includes('image')) {
		accept.push('image/jpeg', 'image/png', 'image/webp', 'image/avif');
	}
	if (types.includes('svg')) {
		accept.push('image/svg+xml', '.svg');
	}
	if (types.includes('document')) {
		accept.push('application/pdf');
	}
	return accept.join(',');
}

/**
 * The content type to declare for a chosen file. Browsers leave `file.type` empty for
 * some SVGs; the extension decides then. The server checks the bytes regardless.
 */
export function contentTypeForFile(file: Pick<File, 'name' | 'type'>): string {
	if (file.type !== '') {
		return file.type;
	}
	const extension = file.name.toLowerCase().split('.').pop() ?? '';
	const byExtension: Record<string, string> = {
		svg: 'image/svg+xml',
		pdf: 'application/pdf',
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png',
		webp: 'image/webp',
		avif: 'image/avif',
	};
	return byExtension[extension] ?? 'application/octet-stream';
}

/** One folder as a flat, indented option, parents before their children. */
export interface MediaFolderOptionModel {
	id: number;
	label: string;
	depth: number;
	brandId: number | null;
}

/** Folders in tree order, for a single select. Orphans (parent not visible) sit at the top level. */
export function flattenMediaFolders(
	folders: ReadonlyArray<MediaFolderDto>,
): Array<MediaFolderOptionModel> {
	const ids = new Set(folders.map((folder) => folder.id));
	const byParent = new Map<number | null, Array<MediaFolderDto>>();
	for (const folder of folders) {
		const parent =
			folder.parentId != null && ids.has(folder.parentId)
				? folder.parentId
				: null;
		const siblings = byParent.get(parent) ?? [];
		siblings.push(folder);
		byParent.set(parent, siblings);
	}

	const result: Array<MediaFolderOptionModel> = [];
	const visit = (parent: number | null, depth: number): void => {
		const children = [...(byParent.get(parent) ?? [])].sort((a, b) =>
			a.name.localeCompare(b.name),
		);
		for (const child of children) {
			result.push({
				id: child.id,
				label: child.name,
				depth,
				brandId: child.brandId,
			});
			// Depth is capped by the server; the guard stops a malformed cycle here.
			if (depth < 10) {
				visit(child.id, depth + 1);
			}
		}
	};
	visit(null, 0);
	return result;
}

/** The brand an item is filed under, in words. */
export function describeMediaBrand(
	brandId: number | null,
	access: MediaAccessDto | null,
): string {
	if (brandId == null) {
		return 'Shared';
	}
	return (
		access?.brands.find((brand) => brand.id === brandId)?.name ??
		'Another brand'
	);
}

/**
 * Where a new upload goes when nothing else says: the brand being browsed, else the
 * user's only brand, else the default brand, else Shared.
 */
export function defaultUploadBrand(
	access: MediaAccessDto,
	browsing: number | 'shared' | undefined,
): number | null {
	if (browsing === 'shared') {
		return access.canManageShared ? null : (access.brands[0]?.id ?? null);
	}
	if (browsing != null) {
		return browsing;
	}
	const preferred =
		access.brands.find((brand) => brand.isDefault) ?? access.brands[0];
	return preferred?.id ?? null;
}
